import {
  fetchClientByClientIdRequest,
  putMirroredFundingSourceForClientByClientIdRequest,
} from '@/model';
import { selectClient } from '@client/model/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { clientsApi, clientKeys } from '@client/api/waterlily';
import { toast } from 'sonner';
import {
  findPartnerLinkedSource,
  splitFundingSourceFields,
} from '@/components/funding-sources-coordination/partner-funding-utils';
import type { AllocationOption } from '@/components/funding-sources-coordination/partner-allocation-section';

export interface PartnerSaveConfig {
  fundingSourceId: string | undefined;
  source: SelfFundingSource;
  isExistingMirror: boolean;
  allocationOption: AllocationOption;
  clientPercent: number;
}

export function useVehiclePartnerActions() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const client = useSelector(selectClient);

  const partnerClientId = client?.partnerClientId ?? null;
  const partnerFirstName = client?.mutableClientPartner?.clientFirstName;
  const partnerSelfFunding = client?.mutableClientPartner?.multipleFundingSources?.selfFunding;

  function dispatchMirrored(
    clientOperation: PutMirroredFundingSourceBodyProps['clientOperation'],
    partnerOperation: PutMirroredFundingSourceBodyProps['partnerOperation'],
  ) {
    if (!partnerClientId || !client?.clientId) return;
    dispatch(
      putMirroredFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        clientOperation,
        partnerClientId,
        partnerOperation,
      }),
    );
  }

  function handleMirrorToPartner(sourceId: string, source: SelfFundingSource) {
    if (!client?.clientId) return;
    const groupId = source.fundingSourceGroupId ?? crypto.randomUUID();
    // Stamp the originator on both copies so the partner is correctly detected
    // as the recipient (and locked to read-only) on their side.
    const mirroredSource = {
      ...source,
      partnerAction: 'mirrored' as const,
      fundingSourceGroupId: groupId,
      addedByClientId: source.addedByClientId ?? client.clientId,
    };

    dispatchMirrored(
      { fundingSourceId: sourceId, fundingSourceData: mirroredSource },
      { fundingSourceId: null, fundingSourceData: mirroredSource },
    );
  }

  function handleSyncToPartner(
    sourceId: string,
    source: SelfFundingSource,
    partnerOverrides?: Partial<SelfFundingSource>,
  ) {
    if (!source.fundingSourceGroupId) return;

    const partnerSourceEntry = findPartnerLinkedSource(
      partnerSelfFunding,
      source.fundingSourceGroupId,
    );
    if (!partnerSourceEntry) return;

    const [partnerFundingSourceId, partnerSource] = partnerSourceEntry;
    const isSplit = source.splitRatio != null;

    // Preserve the existing originator on re-saves; only stamp if missing.
    const addedByClientId =
      source.addedByClientId ?? partnerSource.addedByClientId ?? client?.clientId ?? null;

    dispatchMirrored(
      {
        fundingSourceId: sourceId,
        fundingSourceData: { ...source, addedByClientId },
      },
      {
        fundingSourceId: partnerFundingSourceId,
        fundingSourceData: {
          ...source,
          partnerAction: 'mirrored',
          fundingSourceGroupId: source.fundingSourceGroupId,
          addedByClientId,
          ...(isSplit && {
            existingAssetsValue: partnerSource.existingAssetsValue,
            monthlyContributionAmount: partnerSource.monthlyContributionAmount,
            monthlyIncome: partnerSource.monthlyIncome,
            drawdownAmount: partnerSource.drawdownAmount,
            splitRatio: partnerSource.splitRatio,
          }),
          ...partnerOverrides,
        },
      },
    );
  }

  // The server re-reads both partners' copies, so this never depends on how
  // fresh the partner data cached in the browser is.
  async function unlinkOnServer(
    fundingSourceId: string,
    options: Pick<PutUnlinkPartnerFundingSourceBodyProps, 'ownerUpdates' | 'deleteOwnerCopy'>,
  ) {
    if (!client?.clientId) return;
    try {
      await clientsApi.unlinkPartnerFundingSource({
        clientId: client.clientId,
        fundingSourceId,
        ...options,
      });
    } catch (err) {
      console.error('unlinkPartnerFundingSource failed', err);
      toast.error(
        options.deleteOwnerCopy ? 'Could not delete the shared fund.' : 'Could not unlink the fund.',
      );
      return;
    }
    dispatch(fetchClientByClientIdRequest(client.clientId));
    if (partnerClientId) {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(partnerClientId) });
    }
  }

  /**
   * Only the partner who shared the fund can unlink it. The partner's copy is
   * removed and a split goes back to its full amount. `ownerUpdates` carries
   * form edits submitted alongside the unlink (monetary fields = household totals).
   */
  function handleUnlink(sourceId: string, ownerUpdates?: Partial<SelfFundingSource>) {
    return unlinkOnServer(sourceId, { ownerUpdates });
  }

  function handleDeleteShared(sourceId: string) {
    return unlinkOnServer(sourceId, { deleteOwnerCopy: true });
  }

  function saveWithPartnerSync({
    fundingSourceId,
    source,
    isExistingMirror,
    allocationOption,
    clientPercent,
  }: PartnerSaveConfig) {
    if (!partnerClientId || !client?.clientId) return;

    const isMirrorOption = allocationOption === 'mirror';
    const effectivePercent = allocationOption === 'split-50-50' ? 50 : clientPercent;

    // Reuse the existing partner source ID and group ID when re-saving an existing
    // mirror — never orphan the link by minting a new group.
    const existingGroupId = isExistingMirror ? source.fundingSourceGroupId ?? null : null;
    const existingPartnerEntry = existingGroupId
      ? findPartnerLinkedSource(partnerSelfFunding, existingGroupId)
      : undefined;
    const existingPartnerSourceId = existingPartnerEntry?.[0] ?? null;
    const groupId = existingGroupId ?? crypto.randomUUID();

    // Stamp the originator on both sides. Preserve the existing originator on
    // re-saves so changing a ratio doesn't transfer ownership.
    const addedByClientId = source.addedByClientId ?? client.clientId;

    const baseSource = {
      ...source,
      partnerAction: 'mirrored' as const,
      fundingSourceGroupId: groupId,
      addedByClientId,
    };

    if (isMirrorOption) {
      // Both partners get the full amount.
      const mirroredSource: SelfFundingSource = {
        ...baseSource,
        splitRatio: null,
      };
      dispatchMirrored(
        { fundingSourceId: fundingSourceId ?? null, fundingSourceData: mirroredSource },
        { fundingSourceId: existingPartnerSourceId, fundingSourceData: mirroredSource },
      );
      return;
    }

    // Split: source has the combined totals (modal reconstructs them on open),
    // so split it by the chosen ratio and write each portion to the matching side.
    const { clientOverrides, partnerOverrides } = splitFundingSourceFields(source, effectivePercent);

    dispatchMirrored(
      {
        fundingSourceId: fundingSourceId ?? null,
        fundingSourceData: {
          ...baseSource,
          ...clientOverrides,
          splitRatio: effectivePercent,
        },
      },
      {
        fundingSourceId: existingPartnerSourceId,
        fundingSourceData: {
          ...baseSource,
          ...partnerOverrides,
          splitRatio: 100 - effectivePercent,
        },
      },
    );
  }

  return {
    partnerFirstName,
    partnerClientId,
    handleMirrorToPartner,
    handleSyncToPartner,
    handleUnlink,
    handleDeleteShared,
    saveWithPartnerSync,
  };
}
